/**
 * DebriefingGuideContainer — manages view toggle between short and full guide
 *
 * Wraps ShortDebriefingGuide (default) and the existing full guide view.
 * Both views share the same data via the guide prop, so edits propagate.
 */

import { useState, useCallback } from 'react';
import type { DebriefingGuideRow } from '../../types/debriefing-guide';
import type { DebriefingGuideView } from '../../lib/short-debriefing-guide.types';
import GuideViewToggle from './GuideViewToggle';
import ShortDebriefingGuide from './ShortDebriefingGuide';

interface DebriefingGuideContainerProps {
  guide: DebriefingGuideRow;
  onGuideChange: (guide: DebriefingGuideRow) => void;
  /** Render function for the full guide view (existing long guide content) */
  renderFullGuide: () => React.ReactNode;
  readOnly?: boolean;
  /** Session ID for loading instructor notes */
  sessionId: string;
  /** Instructor's own notes from the session (sessions.instructor_notes) */
  instructorNotes?: string | null;
  /** Called when the view changes so parent can update title etc. */
  onViewChange?: (view: DebriefingGuideView) => void;
}

export default function DebriefingGuideContainer({
  guide,
  onGuideChange,
  renderFullGuide,
  readOnly,
  sessionId,
  instructorNotes,
  onViewChange,
}: DebriefingGuideContainerProps) {
  const [view, setView] = useState<DebriefingGuideView>('short');

  const handleViewChange = useCallback((newView: DebriefingGuideView) => {
    setView(newView);
    onViewChange?.(newView);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [onViewChange]);

  const handleSwitchToFull = useCallback(() => {
    handleViewChange('full');
  }, [handleViewChange]);

  return (
    <div>
      {/* Toggle bar */}
      <div className="mb-3">
        <GuideViewToggle view={view} onChange={handleViewChange} />
      </div>

      {/* View content */}
      {view === 'short' ? (
        <ShortDebriefingGuide
          guide={guide}
          onGuideChange={onGuideChange}
          onSwitchToFull={handleSwitchToFull}
          readOnly={readOnly}
          sessionId={sessionId}
          instructorNotes={instructorNotes}
        />
      ) : (
        <div>
          {renderFullGuide()}
        </div>
      )}
    </div>
  );
}
